import React from 'react';
import VariableRequest from '@requests/global_variables';

import { Button, LoadingButton, Modal } from '@common/index';
import { Stack } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

import { WarningOutlined } from '@mui/icons-material';
import { useTheme } from '../../styles/theme';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
  AppDispatch,
  FlowGlobalVariableDeleteModal,
  FlowGlobalVariableModalVariant,
  State,
  flowActions,
} from '@store/index';

export const DeleteGlobalVariable: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const queryClient = useQueryClient();

  const { service_id } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const globalVariableModal = useSelector(
    (state: State) => state.flow.globalVariableModal
  ) as FlowGlobalVariableDeleteModal | null;

  const key = globalVariableModal?.data?.key;

  const isDeleteMode = globalVariableModal?.type === FlowGlobalVariableModalVariant.DELETE;
  const isOpen = globalVariableModal !== null && isDeleteMode;

  const { mutateAsync: deleteGlobalVariable, isPending: loadingWhileDeletion } = useMutation({
    mutationFn: VariableRequest.deleteVariable,
    onSuccess: (data) => {
      if (!data) return;
      queryClient.invalidateQueries({ queryKey: ['globalVariables', service_id] });
      enqueueSnackbar('Global Variable deleted successfully!', { variant: 'success', autoHideDuration: 3000 });
      closeModal();
    },
    onError: (error) => {
      console.error(error);
      enqueueSnackbar('Failed to delete Global Variable!', {
        variant: 'error',
        autoHideDuration: 10000,
      });

      enqueueSnackbar(error.message, {
        variant: 'error',
        autoHideDuration: 10000,
      });
    },
  });

  const closeModal = () => {
    dispatch(flowActions.setFlowGlobalVariableModal(null));
  };

  const handleDelete = async () => {
    if (!service_id || !isDeleteMode) return;

    const modalData = globalVariableModal?.data;
    if (!modalData) return;

    try {
      await deleteGlobalVariable({ serviceId: service_id, id: modalData.id });
    } catch {}
  };

  const isLoading = loadingWhileDeletion;

  return (
    <Modal
      open={isOpen}
      setOpen={closeModal}
      header='Delete Global Variable'
      background={theme.telnyx.red}
      icon={<WarningOutlined />}
      identifier={`Variable: ${key}`}
      info='WARNING: Deleting this variable will affect any workflows that are using it. Are you sure you want to continue?'
      actions={
        <Stack direction='row' spacing={1}>
          <Button small onClick={closeModal}>
            Cancel
          </Button>
          <LoadingButton loading={isLoading} minWidth='70px' small customColor='red' onClick={handleDelete}>
            Delete
          </LoadingButton>
        </Stack>
      }
    />
  );
};
